import { z } from 'zod';

/**
 * Versioned wire contract between Shoppex and a merchant-owned payment adapter.
 * A literal version makes breaking changes fail closed instead of being guessed.
 */
export const EXTERNAL_PAYMENT_ADAPTER_CONTRACT_VERSION = '2026-08-14' as const;
export { EXTERNAL_PAYMENT_ADAPTER_GATEWAY_PREFIX } from './payment-gateways.js';
export const EXTERNAL_PAYMENT_ADAPTER_TRUST_LEVEL = 'MERCHANT_ATTESTED' as const;
export const EXTERNAL_PAYMENT_ADAPTER_CONFORMANCE_PATH = '/.well-known/shoppex-payment-adapter' as const;

/**
 * Maximum non-archived adapters per shop, matching the manual-gateway cap.
 * Enforced in ExternalPaymentAdapterService.create; the dashboard reads the
 * same constant for its usage badge so the two cannot drift.
 */
export const MAX_EXTERNAL_PAYMENT_ADAPTERS = 10;

const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'Currency must be a three-letter uppercase code.');
const absoluteHttpsUrlSchema = z.string().url().refine(
  (value) => new URL(value).protocol === 'https:',
  'URL must use HTTPS.',
);

const externalPaymentAdapterBaseSchema = z.object({
  version: z.literal(EXTERNAL_PAYMENT_ADAPTER_CONTRACT_VERSION),
});

export const externalPaymentAdapterSessionRequestSchema = externalPaymentAdapterBaseSchema.extend({
  type: z.literal('payment.session.create'),
  data: z.object({
    attempt_id: z.string().uuid(),
    invoice_id: z.string().uuid(),
    amount_minor: z.number().int().safe().positive(),
    currency: currencySchema,
    customer_email: z.string().email().nullable(),
    description: z.string().min(1).max(500),
    success_url: absoluteHttpsUrlSchema,
    cancel_url: absoluteHttpsUrlSchema,
    event_url: absoluteHttpsUrlSchema,
  }),
});

export const externalPaymentAdapterSessionResponseSchema = externalPaymentAdapterBaseSchema.extend({
  provider_reference: z.string().trim().min(1).max(255),
  checkout_url: absoluteHttpsUrlSchema,
  expires_at: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const externalPaymentAdapterEventSchema = externalPaymentAdapterBaseSchema.extend({
  type: z.enum([
    'payment.processing',
    'payment.succeeded',
    'payment.failed',
  ]),
  data: z.object({
    attempt_id: z.string().uuid(),
    provider_reference: z.string().trim().min(1).max(255),
    amount_minor: z.number().int().safe().positive(),
    currency: currencySchema,
    occurred_at: z.iso.datetime({ offset: true }).nullable().optional(),
  }),
});

export const externalPaymentAdapterConformanceRequestSchema = externalPaymentAdapterBaseSchema.extend({
  type: z.literal('adapter.conformance.run'),
  data: z.object({
    challenge_id: z.string().uuid(),
    mode: z.enum(['standard', 'timeout']),
    expected_event: z.object({
      attempt_id: z.string().uuid(),
      provider_reference: z.string().trim().min(1).max(255),
      amount_minor: z.number().int().safe().positive(),
      currency: currencySchema,
    }),
  }),
});

export const externalPaymentAdapterConformanceSampleSchema = z.object({
  scenario: z.enum([
    'valid_event',
    'amount_mismatch',
    'duplicate_event_first',
    'duplicate_event_retry',
  ]),
  webhook_id: z.string().trim().min(1).max(255),
  webhook_timestamp: z.string().trim().min(1).max(32),
  webhook_signature: z.string().trim().min(1).max(1024),
  raw_body: z.string().min(1).max(4096),
});

export const externalPaymentAdapterConformanceResponseSchema = externalPaymentAdapterBaseSchema.extend({
  type: z.literal('adapter.conformance.result'),
  data: z.object({
    challenge_id: z.string().uuid(),
    samples: z.array(externalPaymentAdapterConformanceSampleSchema).length(4),
  }),
});

export type ExternalPaymentAdapterSessionRequest = z.infer<
  typeof externalPaymentAdapterSessionRequestSchema
>;
export type ExternalPaymentAdapterSessionResponse = z.infer<
  typeof externalPaymentAdapterSessionResponseSchema
>;
export type ExternalPaymentAdapterEvent = z.infer<typeof externalPaymentAdapterEventSchema>;
export type ExternalPaymentAdapterConformanceRequest = z.infer<
  typeof externalPaymentAdapterConformanceRequestSchema
>;
export type ExternalPaymentAdapterConformanceSample = z.infer<
  typeof externalPaymentAdapterConformanceSampleSchema
>;
export type ExternalPaymentAdapterConformanceResponse = z.infer<
  typeof externalPaymentAdapterConformanceResponseSchema
>;

export function buildExternalPaymentAdapterConformanceUrl(sessionEndpoint: string): string | null {
  try {
    const url = new URL(sessionEndpoint);
    if (url.protocol !== 'https:') return null;
    url.pathname = EXTERNAL_PAYMENT_ADAPTER_CONFORMANCE_PATH;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
}

// The gateway-key helpers live in payment-gateways.ts: that module is part of
// the checkout client bundle and must not import this one (its relative
// specifier breaks either NodeNext tsc, the tsup DTS build, or Turbopack,
// depending on how it is written). This module is server-only, so re-exporting
// from there is safe.
export {
  buildExternalPaymentAdapterGatewayKey,
  isExternalPaymentAdapterGatewayKey,
  parseExternalPaymentAdapterGatewayKey,
  type ExternalPaymentAdapterGatewayKey,
} from './payment-gateways.js';
